import type { ApiInputToken, MarketRewards } from '@morpho-org/dapp-core'
import type { InferContractRouterOutputs } from '@orpc/contract'
import type { Address } from 'viem'

import type { Contract } from '@/lib/orpc/contracts'

export type MarketRepayDialogOutput = InferContractRouterOutputs<Contract>['market']['repayDialog']

type ActionToken = MarketRepayDialogOutput['market']['loanToken']

/**
 * What the repay dialog reads: the market params `converter.getMarketParams` takes, the tokens in
 * the `ApiInputToken` form `parseApiAsset` reads, and the `MarketForRewards` shape the summary card
 * reads its borrow rewards from.
 */
export interface RepayDialogMarket {
  marketId: MarketRepayDialogOutput['market']['marketId']
  lltv: string
  irmAddress: Address
  oracle: { address: Address }
  loanAsset: ApiInputToken
  collateralAsset: ApiInputToken | null
  state: {
    avgBorrowApy: number | null
    rewards: MarketRewards[]
  }
}

const toAsset = (token: ActionToken, chainId: number): ApiInputToken => ({
  address: token.address,
  chain: { id: chainId },
  name: token.name ?? token.symbol ?? '',
  symbol: token.symbol ?? '',
  decimals: token.decimals,
  logoURI: token.logoUri,
  priceUsd: token.priceUsd
})

/** Maps `market.repayDialog` to what the repay dialog reads. */
export function toRepayDialogMarket(
  { market, borrow }: MarketRepayDialogOutput,
  chainId: number
): RepayDialogMarket {
  return {
    marketId: market.marketId,
    lltv: market.lltv,
    irmAddress: market.irmAddress,
    oracle: { address: market.oracleAddress },
    loanAsset: toAsset(market.loanToken, chainId),
    collateralAsset: market.collateralToken && toAsset(market.collateralToken, chainId),
    state: {
      avgBorrowApy: borrow.avgApy,
      rewards: borrow.rewards.map(({ token, apr }) => ({
        borrowApr: apr,
        asset: {
          address: token.address,
          chain: { id: token.chainId },
          name: token.symbol ?? '',
          symbol: token.symbol ?? '',
          // A reward row shows its symbol, logo and APR; no amount is formatted with these decimals.
          decimals: 18,
          logoURI: token.logoUri
        }
      }))
    }
  }
}
